import React, { useState } from 'react';
import { Play, X, Star, Youtube } from 'lucide-react';
import { videos, artistInfo } from '../mock';

const FEATURED_VIDEO = {
  title: "It Grows",
  thumbnail: "https://img.youtube.com/vi/VjtH_jCA08Q/maxresdefault.jpg",
  youtubeUrl: "https://youtu.be/VjtH_jCA08Q?si=_bLLlrjQXfS66jwS"
};

export const VideoSection = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);

  return (
    <section id="videos" className="py-24 bg-gradient-to-br from-gray-900 via-black to-gray-900 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 mb-4">
              Videos
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-gold-500 mx-auto mb-6"></div>
            <p className="text-xl text-teal-200 max-w-2xl mx-auto">
              Experience live performances, music videos, and behind-the-scenes content
            </p>
          </div>

          {/* Featured Video */}
          <div className="mb-16" data-testid="featured-video">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Star size={18} className="text-gold-400 fill-gold-400" />
              <span className="text-gold-400 font-bold uppercase tracking-widest text-sm">Featured</span>
              <Star size={18} className="text-gold-400 fill-gold-400" />
            </div>
            <div className="grid md:grid-cols-2 gap-8 items-center bg-gradient-to-br from-teal-950/60 to-gray-900/60 border border-gold-500/30 rounded-2xl p-6 md:p-10">
              <div className="group cursor-pointer relative mx-auto" style={{ maxWidth: '320px' }}>
                <div
                  className="relative overflow-hidden rounded-xl bg-black border border-teal-900/30 hover:border-gold-500/60 transition-all duration-300 aspect-[9/16]"
                  onClick={() => window.open(FEATURED_VIDEO.youtubeUrl, '_blank')}
                >
                  <img
                    alt={FEATURED_VIDEO.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                    src={FEATURED_VIDEO.thumbnail}
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <div className="w-20 h-20 bg-gold-500/90 hover:bg-gold-500 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-all shadow-2xl">
                      <Play size={36} className="text-black ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4 text-center md:text-left">
                <h2 className="text-3xl md:text-4xl font-bold text-gold-400">{FEATURED_VIDEO.title}</h2>
                <p className="text-teal-200">Click the video to watch — or jump straight to it on YouTube.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <button
                    onClick={() => window.open(FEATURED_VIDEO.youtubeUrl, '_blank')}
                    className="bg-gold-500 hover:bg-gold-600 text-black px-6 py-3 rounded-full font-semibold transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
                    data-testid="featured-play-btn"
                  >
                    <Play size={18} fill="currentColor" />
                    Watch Now
                  </button>
                  <a
                    href={FEATURED_VIDEO.youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full font-semibold transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
                    data-testid="featured-youtube-btn"
                  >
                    <Youtube size={18} />
                    Watch on YouTube
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Videos Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {videos.map((video) => (
              <div 
                key={video.id}
                className="group cursor-pointer"
                onClick={() => setSelectedVideo(video)}
              >
                <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-800 to-teal-950 border border-teal-900/30 hover:border-teal-700/50 transition-all duration-300">
                  {/* Thumbnail */}
                  <div className="relative aspect-video overflow-hidden">
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                    />
                    <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <div className="w-16 h-16 bg-gold-500/90 hover:bg-gold-500 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-all duration-200 shadow-lg">
                        <Play className="text-black ml-1" size={28} fill="currentColor" />
                      </div>
                    </div>
                  </div>

                  {/* Video Title */}
                  <div className="p-4">
                    <h2 className="text-lg font-semibold text-teal-50 group-hover:text-gold-400 transition-colors duration-200">
                      {video.title}
                    </h2>
                    <p className="text-sm text-teal-300 mt-1 capitalize">
                      {video.platform}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Social Links */}
          <div className="mt-16 text-center space-y-6">
            <p className="text-xl text-teal-200">Follow for more content</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href={artistInfo.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
              >
                Instagram
              </a>
              <a 
                href={artistInfo.socialMedia.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
              >
                YouTube
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gold-400 transition-colors"
            onClick={() => setSelectedVideo(null)}
          >
            <X size={36} />
          </button>
          <div className={`${selectedVideo.platform === 'instagram' ? 'max-w-md' : 'max-w-5xl'} w-full`} onClick={(e) => e.stopPropagation()}>
            {selectedVideo.platform === 'tiktok' ? (
              <div className="text-center space-y-6">
                <p className="text-xl text-teal-200">TikTok videos open in a new tab</p>
                <a 
                  href={selectedVideo.embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-8 py-4 rounded-full font-semibold text-lg hover:scale-105 transition-transform"
                >
                  Watch on TikTok
                </a>
              </div>
            ) : selectedVideo.platform === 'direct' ? (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <video
                  src={selectedVideo.embedUrl}
                  controls
                  autoPlay
                  className="w-full h-full object-contain"
                >
                  Your browser does not support the video tag.
                </video>
              </div>
            ) : selectedVideo.platform === 'instagram' ? (
              <div className="aspect-[9/16] bg-black rounded-lg overflow-hidden max-h-[80vh] mx-auto">
                <iframe 
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            ) : (
              <div className="aspect-video bg-black rounded-lg overflow-hidden">
                <iframe 
                  src={selectedVideo.embedUrl}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            )}
            <h2 className="text-2xl font-bold text-teal-50 mt-4 text-center">
              {selectedVideo.title}
            </h2>
          </div>
        </div>
      )}
    </section>
  );
};
